(() => {
  document.documentElement.classList.add('js');
  const body = document.body;
  const moneyFormat = body?.dataset?.moneyFormat || '{{amount}}';

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number === null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };

  const formatMoney = (cents, format = moneyFormat) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents);
    if (Number.isNaN(value)) return '';

    switch (format.match(/\{\{(\w+)\}\}/)?.[1]) {
      case 'amount':
        return format.replace('{{amount}}', formatWithDelimiters(value, 2));
      case 'amount_no_decimals':
        return format.replace('{{amount_no_decimals}}', formatWithDelimiters(value, 0));
      case 'amount_with_comma_separator':
        return format.replace('{{amount_with_comma_separator}}', formatWithDelimiters(value, 2, '.', ','));
      case 'amount_no_decimals_with_comma_separator':
        return format.replace('{{amount_no_decimals_with_comma_separator}}', formatWithDelimiters(value, 0, '.', ','));
      default:
        return format.replace('{{amount}}', formatWithDelimiters(value, 2));
    }
  };

  const bindOnce = (element, flag, eventName, handler, options) => {
    if (!element || element.dataset?.[flag] === 'true') return;
    element.dataset[flag] = 'true';
    element.addEventListener(eventName, handler, options);
  };

  const makeRevealVisible = () => {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
  };

  const initHeader = () => {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    const desktopNav = header.querySelector('.site-nav--desktop');
    const desktopMq = window.matchMedia('(min-width: 768px)');
    let megaOpen = false;
    let megaOpenTimer = null;
    let megaCloseTimer = null;
    const MEGA_OPEN_DELAY = 45;
    const MEGA_CLOSE_DELAY = 220;

    const syncHeaderHeight = () => {
      const height = header.offsetHeight || 64;
      document.documentElement.style.setProperty('--site-header-height', `${height}px`);
    };

    const syncMegaOpenState = () => {
      if (!desktopNav || !desktopMq.matches) {
        megaOpen = false;
        header.classList.remove('is-mega-open');
        return;
      }
      header.classList.toggle('is-mega-open', megaOpen);
    };

    const setMegaOpen = (open) => {
      megaOpen = Boolean(open);
      syncMegaOpenState();
      updateHeader();
    };

    const scheduleMegaOpen = () => {
      if (megaCloseTimer) window.clearTimeout(megaCloseTimer);
      if (megaOpen) return;
      if (megaOpenTimer) window.clearTimeout(megaOpenTimer);
      megaOpenTimer = window.setTimeout(() => setMegaOpen(true), MEGA_OPEN_DELAY);
    };

    const scheduleMegaClose = () => {
      if (megaOpenTimer) window.clearTimeout(megaOpenTimer);
      if (megaCloseTimer) window.clearTimeout(megaCloseTimer);
      megaCloseTimer = window.setTimeout(() => setMegaOpen(false), MEGA_CLOSE_DELAY);
    };

    const updateHeader = () => {
      const isHome = body?.dataset?.template === 'index';
      syncHeaderHeight();
      syncMegaOpenState();
      if (!isHome) return;
      if (header.classList.contains('is-mega-open')) return;
      if (window.scrollY > 20) {
        header.classList.add('glass-dark', 'border-b', 'border-border/10', 'shadow-lg');
        header.classList.remove('bg-transparent');
      } else {
        header.classList.remove('glass-dark', 'border-b', 'border-border/10', 'shadow-lg');
        header.classList.add('bg-transparent');
      }
    };

    updateHeader();
    if (desktopNav && !desktopNav.dataset.jsMegaStateBound) {
      desktopNav.dataset.jsMegaStateBound = 'true';

      const megaItems = desktopNav.querySelectorAll('.site-nav__item--has-children');

      megaItems.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          scheduleMegaOpen();
        });

        item.addEventListener('mouseleave', () => {
          scheduleMegaClose();
        });

        item.addEventListener('focusin', () => {
          if (megaOpenTimer) window.clearTimeout(megaOpenTimer);
          if (megaCloseTimer) window.clearTimeout(megaCloseTimer);
          setMegaOpen(true);
        });

        item.addEventListener('focusout', (event) => {
          const next = event.relatedTarget;
          if (next && item.contains(next)) return;
          scheduleMegaClose();
        });
      });

      desktopNav.addEventListener('mouseenter', () => {
        if (megaCloseTimer) window.clearTimeout(megaCloseTimer);
      });

      desktopNav.addEventListener('mouseleave', () => {
        scheduleMegaClose();
      });
    }
    if (!window.__themeHeaderScrollBound) {
      window.addEventListener('scroll', updateHeader, { passive: true });
      window.__themeHeaderScrollBound = true;
    }
    if (!window.__themeHeaderResizeBound) {
      window.addEventListener('resize', syncHeaderHeight, { passive: true });
      window.__themeHeaderResizeBound = true;
    }
  };

  const initScrollReveal = () => {
    const isDesignMode = body?.dataset?.designMode === 'true' || window.Shopify?.designMode === true;
    if (isDesignMode || body?.dataset?.enableReveal !== 'true') {
      makeRevealVisible();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      makeRevealVisible();
      return;
    }

    if (!window.__themeRevealObserver) {
      window.__themeRevealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              window.__themeRevealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );
    }

    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (el.classList.contains('is-visible') || el.dataset.revealObserved === 'true') return;
      el.dataset.revealObserved = 'true';
      window.__themeRevealObserver.observe(el);
    });
  };

  const initCart = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    if (!drawer || !overlay) return;

    const itemsList = drawer.querySelector('[data-cart-items-list]');
    const emptyState = drawer.querySelector('[data-cart-empty]');
    const upsells = drawer.querySelector('[data-cart-upsells]');
    const footer = drawer.querySelector('[data-cart-footer]');
    const countText = drawer.querySelector('[data-cart-count-text]');

    const setOpen = (open) => {
      drawer.classList.toggle('is-open', open);
      overlay.classList.toggle('is-visible', open);
      overlay.classList.toggle('hidden', !open);
      body.classList.toggle('overflow-hidden', open);
    };

    const updateCount = (count) => {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.style.display = count > 0 ? '' : 'none';
      });
      if (countText) countText.textContent = count;
    };

    const renderItems = (cart) => {
      updateCount(cart.item_count);

      if (cart.item_count === 0) {
        emptyState?.classList.remove('hidden');
        itemsList?.classList.add('hidden');
        footer?.setAttribute('style', 'display:none');
        upsells?.classList.add('hidden');
        return;
      }

      emptyState?.classList.add('hidden');
      itemsList?.classList.remove('hidden');
      upsells?.classList.remove('hidden');
      footer?.removeAttribute('style');

      if (itemsList) {
        itemsList.innerHTML = cart.items
          .map((item) => {
            const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="text-xs text-muted-foreground">${item.variant_title}</p>` : '';
            return `
            <div class="flex gap-4 p-3 rounded-xl bg-muted/50" data-cart-line data-line-key="${item.key}">
              <img src="${item.image}" alt="${item.product_title}" class="h-20 w-20 rounded-lg object-cover" width="80" height="80">
              <div class="flex-1 min-w-0">
                <a href="${item.url}" class="font-semibold text-sm text-foreground truncate hover:text-accent block">${item.product_title}</a>
                ${variantTitle}
                <p class="text-sm font-bold text-foreground mt-1">${formatMoney(item.final_line_price)}</p>
                <div class="flex items-center gap-2 mt-2">
                  <button type="button" class="h-7 w-7 rounded-md bg-muted flex items-center justify-center hover:bg-border transition-colors active:scale-95" data-qty-minus>
                    <span class="sr-only">-</span>
                    <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
                  </button>
                  <span class="text-sm font-medium w-6 text-center tabular-nums" data-qty>${item.quantity}</span>
                  <button type="button" class="h-7 w-7 rounded-md bg-muted flex items-center justify-center hover:bg-border transition-colors active:scale-95" data-qty-plus>
                    <span class="sr-only">+</span>
                    <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                  </button>
                  <button type="button" class="ml-auto h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors" data-remove>
                    <span class="sr-only">Remove</span>
                    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M6 6l1 14h10l1-14"/></svg>
                  </button>
                </div>
              </div>
            </div>
          `;
          })
          .join('');
      }

      const totalEl = drawer.querySelector('[data-cart-total]');
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
    };

    const fetchCart = async () => {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    };

    const changeLine = async (lineKey, quantity) => {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      const cart = await res.json();
      renderItems(cart);
    };

    const refresh = async () => {
      const cart = await fetchCart();
      renderItems(cart);
    };

    const addItem = async (variantId, quantity = 1) => {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] })
      });
      await refresh();
      setOpen(true);
    };

    document.querySelectorAll('[data-cart-open]').forEach((btn) => {
      bindOnce(btn, 'jsCartOpenBound', 'click', () => setOpen(true));
    });
    document.querySelectorAll('[data-cart-close]').forEach((btn) => {
      bindOnce(btn, 'jsCartCloseBound', 'click', () => setOpen(false));
    });
    bindOnce(overlay, 'jsCartOverlayBound', 'click', () => setOpen(false));

    bindOnce(drawer, 'jsCartDrawerBound', 'click', (event) => {
      const target = event.target.closest('[data-qty-minus],[data-qty-plus],[data-remove]');
      if (!target) return;
      const line = target.closest('[data-cart-line]');
      if (!line) return;
      const lineKey = line.getAttribute('data-line-key');
      const qtyEl = line.querySelector('[data-qty]');
      const qty = Number(qtyEl?.textContent || 1);

      if (target.hasAttribute('data-remove')) {
        changeLine(lineKey, 0);
        return;
      }

      if (target.hasAttribute('data-qty-minus')) {
        changeLine(lineKey, Math.max(0, qty - 1));
      }
      if (target.hasAttribute('data-qty-plus')) {
        changeLine(lineKey, qty + 1);
      }
    });

    document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      bindOnce(btn, 'jsQuickAddBound', 'click', () => {
        if (btn.hasAttribute('disabled')) return;
        const variantId = btn.getAttribute('data-variant-id');
        if (variantId) addItem(variantId, 1);
      });
    });

    window.themeCart = { open: () => setOpen(true), close: () => setOpen(false), addItem, refresh };
  };

  const initSearch = () => {
    const overlay = document.querySelector('[data-search-overlay]');
    if (!overlay) return;

    const input = overlay.querySelector('[data-search-input]');
    const results = overlay.querySelector('[data-search-results]');
    const list = overlay.querySelector('[data-search-list]');
    const count = overlay.querySelector('[data-search-count]');
    const empty = overlay.querySelector('[data-search-empty]');
    const popular = overlay.querySelector('[data-search-popular]');
    const template = overlay.querySelector('[data-search-item-template]');

    const open = () => {
      overlay.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
      setTimeout(() => input?.focus(), 100);
    };

    const close = () => {
      overlay.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
      if (input) input.value = '';
      if (results) results.classList.add('hidden');
      if (popular) popular.classList.remove('hidden');
    };

    document.querySelectorAll('[data-search-open]').forEach((btn) => bindOnce(btn, 'jsSearchOpenBound', 'click', open));
    document.querySelectorAll('[data-search-close]').forEach((btn) => bindOnce(btn, 'jsSearchCloseBound', 'click', close));

    if (!window.__themeSearchEscBound) {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
      window.__themeSearchEscBound = true;
    }

    overlay.querySelectorAll('[data-search-term]').forEach((btn) => {
      bindOnce(btn, 'jsSearchTermBound', 'click', () => {
        if (!input) return;
        input.value = btn.getAttribute('data-search-term') || '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });

    const renderResults = (products, query) => {
      if (!results || !list || !count || !empty || !template) return;
      list.innerHTML = '';
      count.textContent = `${products.length} Ergebnis${products.length !== 1 ? 'se' : ''}`;

      if (products.length === 0) {
        empty.textContent = `Keine Ergebnisse für „${query}“`;
        empty.classList.remove('hidden');
        return;
      }

      empty.classList.add('hidden');
      products.forEach((product) => {
        const node = template.content.cloneNode(true);
        const anchor = node.querySelector('a');
        const image = node.querySelector('[data-search-item-image]');
        const title = node.querySelector('[data-search-item-title]');
        const meta = node.querySelector('[data-search-item-meta]');
        const price = node.querySelector('[data-search-item-price]');
        const imageUrl = product.featured_image?.url || product.image || '';
        const numericPrice = Number(product.price_min || product.price || 0);
        const cents = Number.isFinite(numericPrice) ? Math.round(numericPrice * 100) : 0;

        if (anchor) anchor.href = product.url;
        if (image) image.src = imageUrl;
        if (image) image.alt = product.title;
        if (title) title.textContent = product.title;
        if (meta) meta.textContent = product.product_type || '';
        if (price) price.textContent = formatMoney(cents);

        list.appendChild(node);
      });
    };

    let controller = null;
    const onInput = async (event) => {
      const query = event.target.value.trim();
      if (!query || query.length < 2) {
        results?.classList.add('hidden');
        popular?.classList.remove('hidden');
        return;
      }

      results?.classList.remove('hidden');
      popular?.classList.add('hidden');

      if (controller) controller.abort();
      controller = new AbortController();

      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide`;
      try {
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        renderResults(products, query);
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    };

    bindOnce(input, 'jsSearchInputBound', 'input', onInput);
  };

  const initCollectionFilters = () => {
    const form = document.getElementById('CollectionFiltersForm');
    if (!form) return;

    const collectParams = () => {
      const params = new URLSearchParams();
      const controls = new Set();

      Array.from(form.elements || []).forEach((element) => controls.add(element));
      document.querySelectorAll('[form="CollectionFiltersForm"]').forEach((element) => controls.add(element));

      controls.forEach((control) => {
        if (!control?.name || control.disabled) return;
        const type = (control.type || '').toLowerCase();
        if (type === 'submit' || type === 'button' || type === 'file') return;
        if ((type === 'checkbox' || type === 'radio') && !control.checked) return;
        const value = `${control.value ?? ''}`.trim();
        if (value === '') return;
        params.append(control.name, value);
      });

      return params;
    };

    const submitFilters = () => {
      const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      const params = collectParams();
      const query = params.toString();
      const nextUrl = `${url.pathname}${query ? `?${query}` : ''}`;
      window.location.assign(nextUrl);
    };

    bindOnce(form, 'jsFiltersSubmitBound', 'submit', (event) => {
      event.preventDefault();
      submitFilters();
    });

    bindOnce(form, 'jsFiltersChangeBound', 'change', submitFilters);
    const sortSelect = document.querySelector('[data-collection-sort]');
    bindOnce(sortSelect, 'jsSortBound', 'change', submitFilters);

    document.querySelectorAll('[form="CollectionFiltersForm"]').forEach((control) => {
      bindOnce(control, 'jsFilterControlBound', 'change', submitFilters);
      if (control.matches('input[type="number"]')) {
        bindOnce(control, 'jsFilterNumberBound', 'keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submitFilters();
          }
        });
      }
    });

    document.querySelectorAll('[data-filter-toggle]').forEach((btn) => {
      bindOnce(btn, 'jsFilterToggleBound', 'click', () => {
        const panel = document.querySelector('[data-filter-panel]');
        panel?.classList.toggle('hidden');
      });
    });
  };

  const initProductPage = () => {
    const gallery = document.querySelector('[data-product-gallery]');
    const mainImage = gallery?.querySelector('[data-main-image]');
    const setActiveThumb = (thumb) => {
      if (!gallery || !thumb) return;
      gallery.querySelectorAll('[data-thumb]').forEach((btn) => {
        btn.classList.remove('border-accent', 'neon-glow-box');
        btn.classList.add('border-transparent', 'opacity-60');
      });
      thumb.classList.add('border-accent', 'neon-glow-box');
      thumb.classList.remove('border-transparent', 'opacity-60');
    };

    const activateVariantMedia = (mediaId, mediaSrc) => {
      if (!gallery || !mainImage) return;
      const thumbs = Array.from(gallery.querySelectorAll('[data-thumb]'));
      if (thumbs.length === 0) {
        if (mediaSrc) mainImage.src = mediaSrc;
        return;
      }

      let targetThumb = null;
      if (mediaId) {
        targetThumb = thumbs.find((thumb) => thumb.getAttribute('data-media-id') === mediaId);
      }
      if (!targetThumb && mediaSrc) {
        targetThumb = thumbs.find((thumb) => thumb.getAttribute('data-media-src') === mediaSrc);
      }

      if (targetThumb) {
        const src = targetThumb.getAttribute('data-media-src');
        if (src) mainImage.src = src;
        setActiveThumb(targetThumb);
        return;
      }

      if (mediaSrc) {
        mainImage.src = mediaSrc;
        gallery.querySelectorAll('[data-thumb]').forEach((btn) => {
          btn.classList.remove('border-accent', 'neon-glow-box');
          btn.classList.add('border-transparent', 'opacity-60');
        });
      }
    };

    if (gallery) {
      gallery.querySelectorAll('[data-thumb]').forEach((thumb) => {
        bindOnce(thumb, 'jsThumbBound', 'click', () => {
          const src = thumb.getAttribute('data-media-src');
          if (src && mainImage) mainImage.src = src;
          setActiveThumb(thumb);
        });
      });
    }

    const variantButtons = document.querySelector('[data-variant-buttons]');
    const variantInput = document.querySelector('[data-variant-id-input]');
    const priceEl = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-product-compare]');

    if (variantButtons && variantInput) {
      bindOnce(variantButtons, 'jsVariantBound', 'click', (event) => {
        const btn = event.target.closest('[data-variant-id]');
        if (!btn || btn.hasAttribute('disabled')) return;
        const id = btn.getAttribute('data-variant-id');
        const price = btn.getAttribute('data-variant-price');
        const compare = btn.getAttribute('data-variant-compare');
        const mediaId = btn.getAttribute('data-variant-media-id');
        const mediaSrc = btn.getAttribute('data-variant-media-src');
        if (id) variantInput.value = id;

        variantButtons.querySelectorAll('[data-variant-id]').forEach((el) => {
          el.classList.remove('border-accent', 'bg-accent/10', 'text-foreground', 'neon-glow-box');
          el.classList.add('border-border', 'text-muted-foreground');
        });
        btn.classList.add('border-accent', 'bg-accent/10', 'text-foreground', 'neon-glow-box');
        btn.classList.remove('border-border', 'text-muted-foreground');

        if (priceEl && price) priceEl.textContent = price;
        if (compareEl) {
          if (compare) {
            compareEl.textContent = compare;
            compareEl.classList.remove('hidden');
          } else {
            compareEl.classList.add('hidden');
          }
        }

        activateVariantMedia(mediaId, mediaSrc);
      });

      const initialVariant = variantButtons.querySelector('[data-variant-id].border-accent') || variantButtons.querySelector('[data-variant-id]');
      if (initialVariant) {
        activateVariantMedia(
          initialVariant.getAttribute('data-variant-media-id'),
          initialVariant.getAttribute('data-variant-media-src')
        );
      }
    }

    document.querySelectorAll('[data-qty-minus],[data-qty-plus]').forEach((btn) => {
      bindOnce(btn, 'jsProductQtyBound', 'click', () => {
        const input = btn.closest('form')?.querySelector('[data-qty-input]');
        if (!input) return;
        const current = Number(input.value) || 1;
        if (btn.hasAttribute('data-qty-minus')) {
          input.value = Math.max(1, current - 1);
        }
        if (btn.hasAttribute('data-qty-plus')) {
          input.value = current + 1;
        }
      });
    });

    document.querySelectorAll('[data-product-form], form[action*="/cart/add"]').forEach((form) => {
      bindOnce(form, 'jsProductFormBound', 'submit', async (event) => {
        event.preventDefault();
        const formData = new FormData(form);
        const variantId = Number(formData.get('id'));
        if (!variantId) return;
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData
          });
          if (!res.ok) throw new Error('Cart add request failed');
          if (window.themeCart?.refresh) {
            await window.themeCart.refresh();
            window.themeCart.open();
          }
        } catch (err) {
          console.error(err);
        } finally {
          if (submitButton) submitButton.disabled = false;
        }
      });
    });
  };

  const initFeaturedSliders = () => {
    document.querySelectorAll('[data-featured-slider]').forEach((slider) => {
      if (slider.dataset.jsFeaturedInit === 'true') return;
      slider.dataset.jsFeaturedInit = 'true';
      const track = slider.querySelector('[data-featured-track]');
      const prev = slider.querySelector('[data-featured-prev]');
      const next = slider.querySelector('[data-featured-next]');
      if (!track || !prev || !next) return;

      const step = () => Math.max(180, Math.round(track.clientWidth * 0.7));

      prev.addEventListener('click', () => {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });

      next.addEventListener('click', () => {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    });
  };

  const runThemeInitializers = () => {
    initHeader();
    initScrollReveal();
    initCart();
    initSearch();
    initCollectionFilters();
    initProductPage();
    initFeaturedSliders();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runThemeInitializers);
  } else {
    runThemeInitializers();
  }

  document.addEventListener('shopify:section:load', runThemeInitializers);
  document.addEventListener('shopify:section:reorder', runThemeInitializers);
  document.addEventListener('shopify:block:select', runThemeInitializers);
})();
